/* Small pure helpers for forms: dates of birth, codes typed on Arabic keyboards, password strength. */
import { ageDecision, type AgeDecision } from '@pathabroad/core/browser';

const pad = (n: number) => String(n).padStart(2, '0');

export const daysInMonth = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();

/* "YYYY-MM-DD" for a real calendar day, otherwise null (31 February, missing parts) */
export function toISODate(year: number | string, month: number | string, day: number | string): string | null {
  const y = Number(year), m = Number(month), d = Number(day);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  if (y < 1000 || y > 9999 || m < 1 || m > 12 || d < 1 || d > daysInMonth(y, m)) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}

export function splitISODate(iso: string | undefined | null): { y: string; m: string; d: string } {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? { y: m[1], m: String(Number(m[2])), d: String(Number(m[3])) } : { y: '', m: '', d: '' };
}

/* today's date where the student is (the server re-checks with its own clock) */
export function localTodayISO(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/* every year from this one back 100 years: the list itself mustn't hint at any age limit */
export function birthYears(now: Date = new Date()): number[] {
  const y = now.getFullYear();
  return Array.from({ length: 101 }, (_, i) => y - i);
}

export type DobResult = { ok: true; dob: string; decision: AgeDecision } | { ok: false; error: 'dob.missing' | 'dob.no_such_day' | 'dob.check' };
export function checkDob(year: string, month: string, day: string, now: Date = new Date()): DobResult {
  if (!year || !month || !day) return { ok: false, error: 'dob.missing' };
  const dob = toISODate(year, month, day);
  if (!dob) return { ok: false, error: 'dob.no_such_day' };
  const decision = ageDecision(dob, localTodayISO(now));
  if (decision.reason === 'invalid') return { ok: false, error: 'dob.check' };
  return { ok: true, dob, decision };
}

/* Arabic-Indic (٠-٩) and Persian (۰-۹) digits become 0-9, so codes typed on an Arabic keyboard still work */
export function asciiDigits(s: string): string {
  return s.replace(/[٠-٩]/g, c => String(c.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, c => String(c.charCodeAt(0) - 0x06F0));
}
export const sixDigits = (s: string) => asciiDigits(s).replace(/\D/g, '').slice(0, 6);

export const MIN_PASSWORD = 10;
export type Strength = 'too_short' | 'weak' | 'fair' | 'strong';
const COMMON = /(password|passwort|qwerty|azerty|123456|abcdef|letmein|welcome|iloveyou|admin|pathabroad|pathsabroad)/i;
export function passwordStrength(pw: string): Strength {
  if ([...pw].length < MIN_PASSWORD) return 'too_short';
  const unique = new Set(pw.toLowerCase()).size;
  if (unique < 5 || COMMON.test(pw) || /^(.)\1+$/.test(pw)) return 'weak';
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter(r => r.test(pw)).length;
  const len = [...pw].length;
  if ((len >= 16 && classes >= 2) || (len >= 12 && classes >= 3)) return 'strong';
  if (classes >= 2 || len >= 14) return 'fair';
  return 'weak';
}

export const looksLikeEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
